import moment from 'moment';
import React, {useEffect, useState} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Container, Typography, Box, Paper, Button, Autocomplete, TextField, Grid, Tooltip, IconButton, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import DiscountIcon from '@mui/icons-material/Discount';
import UpdateIcon from '@mui/icons-material/Update';

import Loading from '../../utils/Loading';
import MultiSelect from '../../utils/MultiSelect';
import ClientContact from '../../utils/ClientContact';
import ServiceChip from '../../utils/ServiceChip';
import { displayClientName, handleIntegerField } from '../../utils/Utils';
import { getOpsBooking, updateBooking } from './BookingsSlice';
import { getFleet } from '../fleet/FleetSlice';

function BookingUpdate () {
    const { isLoading, opsBooking, insurances, extras, countries } = useSelector((state) => state.bookings);
    const { fleet, locations } = useSelector((state) => state.fleet);
    const { bookingRef, sourceComponent } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [customerProfileOpen, setCustomerProfileOpen] = useState(false);

    const [vehicle, setVehicle] = useState(opsBooking?.vehicle || {});
    const [pickupLocation, setPickupLocation] = useState(opsBooking?.pickup_location || null);
    const [pickupDateTime, setPickupDateTime] = useState(opsBooking?.pickup_datetime ? moment(opsBooking.pickup_datetime) : null);
    const [deliveryAddress, setDeliveryAddress] = useState(opsBooking?.delivery_address || '');
    const [dropoffLocation, setDropoffLocation] = useState(opsBooking?.dropoff_location || null);
    const [dropoffDateTime, setDropoffDateTime] = useState(opsBooking?.dropoff_datetime ? moment(opsBooking.dropoff_datetime) : null);
    const [collectionAddress, setCollectionAddress] = useState(opsBooking?.collection_address || '');
    const [flightNumber, setFlightNumber] = useState(opsBooking?.flight_number || '');
    const [insurance, setInsurance] = useState(opsBooking?.insurance_waiver || null);
    const [newExtras, setNewExtras] = useState(opsBooking?.extras || []);
    const [babySeats, setBabySeats] = useState(opsBooking?.baby_seats || 0);
    const [boosterSeats, setBoosterSeats] = useState(opsBooking?.booster_seats || 0);
    const [mainDriverName, setMainDriverName] = useState(opsBooking?.main_driver_name || '');
    const [mainDriverLicence, setMainDriverLicence] = useState(opsBooking?.main_driver_licence || '');
    const [mainDriverCountry, setMainDriverCountry] = useState(opsBooking?.main_driver_country || null);
    const [remarks, setRemarks] = useState(opsBooking?.remarks || '');
    const [status, setStatus] = useState(opsBooking?.status || '');

    useEffect(() => {
        let mounted = true;

        if (!opsBooking || Object.keys(opsBooking).length === 0 || opsBooking.reference !== bookingRef) {
            dispatch(getOpsBooking(bookingRef));
        }
        if (!fleet || Object.keys(fleet).length === 0) {
            dispatch(getFleet());
        };
        if (mounted && opsBooking) {
            if (opsBooking.vehicle) {
                setVehicle(opsBooking.vehicle);
            }
            if (opsBooking.pickup_location) {
                setPickupLocation(opsBooking.pickup_location);
            }
            if (opsBooking.pickup_datetime) {
                setPickupDateTime(moment(opsBooking.pickup_datetime));
            }
            if (opsBooking.delivery_address !== undefined) {
                setDeliveryAddress(opsBooking.delivery_address || '');
            }
            if (opsBooking.dropoff_location) {
                setDropoffLocation(opsBooking.dropoff_location);
            }
            if (opsBooking.dropoff_datetime) {
                setDropoffDateTime(moment(opsBooking.dropoff_datetime));
            }
            if (opsBooking.collection_address !== undefined) {
                setCollectionAddress(opsBooking.collection_address || '');
            }
            if (opsBooking.flight_number !== undefined) {
                setFlightNumber(opsBooking.flight_number || '');
            }
            setInsurance(opsBooking.insurance_waiver || null);
            if (opsBooking.extras) {
                setNewExtras(opsBooking.extras);
            }
            if (opsBooking.baby_seats !== undefined) {
                setBabySeats(opsBooking.baby_seats);
            }
            if (opsBooking.booster_seats !== undefined) {
                setBoosterSeats(opsBooking.booster_seats);
            }
            if (opsBooking.main_driver_name !== undefined) {
                setMainDriverName(opsBooking.main_driver_name || '');
            }
            if (opsBooking.main_driver_licence !== undefined) {
                setMainDriverLicence(opsBooking.main_driver_licence || '');
            }
            if (opsBooking.main_driver_country) {
                setMainDriverCountry(opsBooking.main_driver_country);
            }
            if (opsBooking.remarks !== undefined) {
                setRemarks(opsBooking.remarks || '');
            }
            if (opsBooking.status !== undefined) {
                setStatus(opsBooking.status);
            }
        }
        return () => { mounted = false; };
    }, [opsBooking, fleet, bookingRef, dispatch]);

    const handleUpdate = () => {
        const pickupDateTimeString = pickupDateTime ? pickupDateTime.toISOString().slice(0, 23).replace('.000', 'Z') : null;
        const dropoffDateTimeString = dropoffDateTime ? dropoffDateTime.toISOString().slice(0, 23).replace('.000', 'Z') : null;
        const bookingData = {
            id: opsBooking?.id,
            action: 'ops'
        }
        if (opsBooking?.vehicle !== vehicle || (pickupDateTimeString && opsBooking?.pickup_datetime !== pickupDateTimeString) || (dropoffDateTimeString && opsBooking?.dropoff_datetime !== dropoffDateTimeString)) {
            bookingData.vehicle = vehicle;
            if (pickupDateTimeString) bookingData.pickup_datetime = pickupDateTimeString.replace('Z', '+00:00');
            if (dropoffDateTimeString) bookingData.dropoff_datetime = dropoffDateTimeString.replace('Z', '+00:00');
        }
        if (pickupLocation !== 'Delivery' && opsBooking?.pickup_location !== pickupLocation) {
            bookingData.pickup_location = pickupLocation;
        }
        if (opsBooking?.delivery_address !== deliveryAddress) {
            bookingData.delivery_address = deliveryAddress;
        }
        if (dropoffLocation !== 'Collection' && opsBooking?.dropoff_location !== dropoffLocation) {
            bookingData.dropoff_location = dropoffLocation;
        }
        if (opsBooking?.collection_address !== collectionAddress) {
            bookingData.collection_address = collectionAddress;
        }
        if (opsBooking?.flight_number !== flightNumber) {
            bookingData.flight_number = flightNumber;
        }
        if (opsBooking?.insurance_waiver !== insurance) {
            bookingData.insurance_waiver = insurance;
        }
        if (opsBooking?.extras !== newExtras) {
            bookingData.extras = newExtras;
        }
        if (opsBooking?.baby_seats !== babySeats) {
            bookingData.baby_seats = babySeats;
        }
        if (opsBooking?.booster_seats !== boosterSeats) {
            bookingData.booster_seats = boosterSeats;
        }
        if (opsBooking?.main_driver_name !== mainDriverName && mainDriverName !== '') {
            bookingData.main_driver_name = mainDriverName;
        }
        if (opsBooking?.main_driver_licence !== mainDriverLicence && mainDriverLicence !== '') {
            bookingData.main_driver_licence = mainDriverLicence;
        }
        if (opsBooking?.main_driver_country !== mainDriverCountry && mainDriverCountry !== null) {
            bookingData.main_driver_country = mainDriverCountry;
        }
        if (opsBooking?.remarks !== remarks) {
            bookingData.remarks = remarks;
        }
        if (opsBooking?.status !== status) {
            bookingData.status = status;
        }
        if (Object.keys(bookingData).length > 2) {
            dispatch(updateBooking(bookingData, 'ops'));
            if (sourceComponent === 'operations') {
                navigate('/manage/ops');
            } else if (sourceComponent === 'planner') {
                navigate('/manage/planner');
            } else {
                console.error('Unrecognized source component')
                console.log(sourceComponent)
                navigate('/dashboard');
            }
        } else {
            toast.info('No changes');
        }
    }

    const handleNewExtras = (event) => {
        const {target: {value}, } = event;
        setNewExtras(typeof value === 'string' ? value.split(',') : value);
    }

    //Autocomplete settings
    const getVehicleOptionLabel = (option) => {
        if (!option) {
            return '';
        }
        return `${option.registration || ''} ${option.model?.vehicle_make || ''} ${option.model?.vehicle_model || ''}`.trim();
    };
    const getLocationOptionLabel = (option) => {
        if (!option) {
            return '';
        }
        return option.location || '';
    };
    const getCountryOptionLabel = (option) => {
        if (!option) {
            return '';
        }
        return option.name || '';
    };
    const isOptionEqualtoValue = (option, value) => {
        if (!option || !value) {
            return option === value;
        }
        return option.id === value.id;
    };
    
    return (
        <Container>
            <Typography variant='h3' align='center' gutterBottom>
                Update booking
            </Typography>
            <Box sx={{display: 'flex', justifyContent:'center', alignItems: 'center'}}>
                {isLoading ? <Loading /> : null}
            </Box>
            {!isLoading && opsBooking && Object.keys(opsBooking).length > 0 && fleet && Object.keys(fleet).length > 0 ? (
                <Container>
                    {/* Header */}
                    <Paper sx={{padding: 2, margin: 2}}>
                        <Box display='flex' alignItems='center' gap={1}>
                            <Typography variant='h6' gutterBottom sx={{marginBottom: '20px'}}>
                                Booking {opsBooking.reference} | 
                            </Typography>
                            <Button onClick={()=> setCustomerProfileOpen(true)} color='info' sx={{marginBottom: '20px'}}>{displayClientName(opsBooking.client)}</Button>
                            {opsBooking?.service_type && <ServiceChip serviceType={opsBooking.service_type} />}
                        </Box>
                        <Grid container spacing={2} align='center'>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={Array.isArray(fleet) ? fleet : []}
                                    value={vehicle || null}
                                    onChange={(e, newValue) => setVehicle(newValue)}
                                    renderInput={(params) => <TextField {...params} label='Assigned car' required />}
                                    getOptionLabel={getVehicleOptionLabel}
                                    isOptionEqualToValue={isOptionEqualtoValue}
                                    sx={{flexGrow: 1}}
                                    disableClearable
                                />
                            </Grid>
                            <Grid item xs={6} md={3} align='center'>
                                <FormControl>
                                    <InputLabel id='id-status'>Status</InputLabel>
                                    <Select
                                        labelId='id-status'
                                        value={status}
                                        label='Status'
                                        onChange={(e) => setStatus(e.target.value.toString())}
                                    >
                                        <MenuItem value={1}>Active</MenuItem>
                                        <MenuItem value={2}>Dispatched</MenuItem>
                                        <MenuItem value={3}>Completed</MenuItem>
                                        <MenuItem value={4}>Cancelled</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6} md={3} align='right'>
                                <Tooltip title='Add discount'>
                                    <IconButton onClick={() => navigate(`/manage/bookings/${bookingRef}/discounts`)}>
                                        <DiscountIcon color='secondary' />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>                        
                    </Paper>
                    {/* Pickup and Dropoff details */}
                    <Paper sx={{padding: 2, margin: 2}}>
                        <Grid container spacing={2} align='left'>
                            <Grid item xs={12} align='left'>
                                <Typography variant='subtitle1' gutterBottom>
                                    Pick-up
                                </Typography>
                            </Grid>     
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={Array.isArray(locations) ? locations : []}
                                    value={pickupLocation || null}
                                    onChange={(e, newValue) => setPickupLocation(newValue)}
                                    renderInput={(params) => <TextField {...params} label='Pick-up location' />}
                                    getOptionLabel={getLocationOptionLabel}
                                    isOptionEqualToValue={isOptionEqualtoValue}
                                    sx={{flexGrow: 1}}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DateTimePicker
                                    label='Pick-up date and time'
                                    value={pickupDateTime}
                                    onChange={(newValue, context) => setPickupDateTime(moment(newValue))}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label='Delivery address' value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} fullWidth multiline maxRows={5} />
                            </Grid>
                            <Grid item xs={12} align='left'>
                                <Typography variant='subtitle1' gutterBottom>
                                    Drop-off
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Autocomplete
                                    options={Array.isArray(locations) ? locations : []}
                                    value={dropoffLocation || null}
                                    onChange={(e, newValue) => setDropoffLocation(newValue)}
                                    renderInput={(params) => <TextField {...params} label='Drop-off location' />}
                                    getOptionLabel={getLocationOptionLabel}
                                    isOptionEqualToValue={isOptionEqualtoValue}
                                    sx={{flexGrow: 1}}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DateTimePicker
                                    label='Drop-off date and time'
                                    value={dropoffDateTime}
                                    onChange={(newValue, context) => setDropoffDateTime(moment(newValue))}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label='Collection address' value={collectionAddress} onChange={(e) => setCollectionAddress(e.target.value)} fullWidth multiline maxRows={5} />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label='Flight details' value={flightNumber} onChange={(e) => setFlightNumber(e.target.value)} fullWidth multiline maxRows={3} />
                            </Grid>
                        </Grid>
                    </Paper>
                    {/* Insurance and Extras */}
                    <Paper sx={{padding: 2, margin: 2}}>
                        <Grid container spacing={2} align='left' alignItems='center'>
                            <Grid item xs={12} align='left'>
                                <Typography variant='subtitle1' gutterBottom>
                                    Insurance and extras
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel id='id-insurance-waiver'>Insurance waiver</InputLabel>
                                    <Select
                                        labelId='id-insurance-waiver'
                                        id='insurance-waiver'
                                        value={Array.isArray(insurances) && insurance ? (insurances.find((waiver) => waiver?.id === insurance?.id) || '') : ''}
                                        label={insurance?.code || ''}
                                        onChange={(e) => setInsurance(e.target.value || null)}
                                    >
                                        <MenuItem value=''>None</MenuItem>
                                        {Array.isArray(insurances) && insurances.map((waiver) => (
                                            <MenuItem key={waiver?.id} value={waiver}>
                                                {waiver?.label || waiver?.code}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MultiSelect
                                    inputLabel={'Extras'}
                                    values={Array.isArray(extras) ? extras : []}
                                    valuesState={Array.isArray(newExtras) ? newExtras : []}
                                    labelFunction={(value) => {return value?.name || value?.code || ''}}
                                    onChange={handleNewExtras}
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField label='Baby seats' type='number' value={babySeats} onChange={(e) => handleIntegerField(e.target.value, setBabySeats)} sx={{marginBottom: 2}} />
                                <TextField label='Booster seats' type='number' value={boosterSeats} onChange={(e) => handleIntegerField(e.target.value, setBoosterSeats)} />
                            </Grid>
                        </Grid>
                    </Paper>
                    {/* Other items */}
                    <Paper sx={{padding: 2, margin: 2}}>
                        <Grid container spacing={2} align='left' alignItems='center'>
                            <Grid item xs={12} align='left'>
                                <Typography variant='subtitle1' gutterBottom>
                                    Other booking items
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label='Main driver name' value={mainDriverName} onChange={(e) => setMainDriverName(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label='Main driver licence' value={mainDriverLicence} onChange={(e) => setMainDriverLicence(e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Autocomplete
                                    options={Array.isArray(countries) ? countries : []}
                                    value={mainDriverCountry || null}
                                    onChange={(e, newValue) => setMainDriverCountry(newValue)}
                                    getOptionLabel={getCountryOptionLabel}
                                    isOptionEqualToValue={isOptionEqualtoValue}
                                    renderInput={(params) => <TextField {...params} label='Main driver country' />}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField label='Remarks' value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline maxRows={5} />
                            </Grid>
                        </Grid>
                    </Paper>
                    <Grid container spacing={2} align='left'>
                        <Grid item xs={12}>
                            <Button onClick={handleUpdate} variant='contained' endIcon={<UpdateIcon />}>Update</Button>
                        </Grid>
                    </Grid>
                </Container>
            ) : null}
            <Dialog open={customerProfileOpen} onClose={() => setCustomerProfileOpen(false)} aria-labelledby='alert-dialog-title' aria-describedby='alert-dialog-content'>
                <DialogTitle id='alert-dialog-title'>{'Customer Profile'}</DialogTitle>
                <DialogContent>
                    {opsBooking && Object.keys(opsBooking).length > 0 && opsBooking?.client ? <>
                        <ClientContact client={opsBooking?.client} />
                    </> : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCustomerProfileOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

export default BookingUpdate;